import { inject, Injectable, signal } from '@angular/core';
import { collection, Firestore, getDocs, limit, query, where } from '@angular/fire/firestore';
import { COMMONS_ENV } from '@damofire/data-angular-commons';
import { defer, from, Observable, of } from 'rxjs';
import { ItemProductDatabase } from '../db/item-product.db';
import { isConnectionError, notifyPersistenceFailure, withConnectionRetry } from '../db/dexie-hardening';
import { ItemProduct } from '../models/item-product.model';
import { getRaw, HttpUtils, WH_PRODUCTS_COLLECTION, logger } from '@damofire/data-commons';

const DEFAULT_SKU_LIMIT = 20;
const BUNDLE_ENDPOINT = 'nexus-surveys/item-products/bundle';
const META_VERSION_KEY = 'bundleVersion';
const DEXIE_WRITE_CHUNK_SIZE = 5000;

interface BundleManifest {
  url: string;
  version: string;
  generatedAt: number;
  count: number;
  sizeBytes: number;
}

interface BundlePayload {
  version: string;
  generatedAt: number;
  count: number;
  items: ItemProduct[];
}

function buildPrefixEnd(upper: string): string {
  const lastChar = upper[upper.length - 1];
  return upper.slice(0, -1) + String.fromCharCode(lastChar.charCodeAt(0) + 1);
}

@Injectable({ providedIn: 'root' })
export class ItemProductService {
  private readonly firestore = inject(Firestore);
  private readonly db = inject(ItemProductDatabase);
  private readonly environment = inject(COMMONS_ENV);

  readonly localCount = signal(0);
  readonly refreshing = signal(false);
  readonly localVersion = signal<string | null>(null);

  private localDataReady?: Promise<void>;

  searchSkus(term: string, resultLimit = DEFAULT_SKU_LIMIT): Observable<ItemProduct[]> {
    const trimmed = term?.trim();
    if (!trimmed) {
      return of([]);
    }

    const upper = trimmed.toUpperCase();
    const lower = trimmed.toLowerCase();
    const end = buildPrefixEnd(upper);

    return defer(() => from(this.executeSearchQuery(trimmed, upper, lower, end, resultLimit)));
  }

  ensureLocalData(): Promise<void> {
    this.localDataReady ??= this.loadLocalData();
    return this.localDataReady;
  }

  private async loadLocalData(): Promise<void> {
    try {
      const count = await this.db.items.count();
      if (count > 0) {
        this.localCount.set(count);
        this.localVersion.set(await this.readLocalVersion());
        return;
      }
      await this.refreshLocalSkus();
    } catch (err) {
      logger.error('Failed to load local item products', { category: 'sync', error: err });
      this.localDataReady = undefined;
    }
  }

  async refreshLocalSkus(): Promise<{ count: number; version: string; alreadyUpToDate: boolean }> {
    if (this.refreshing()) {
      return { count: this.localCount(), version: this.localVersion() ?? '', alreadyUpToDate: true };
    }
    this.refreshing.set(true);

    try {
      const manifest = await this.fetchBundleManifest();

      const [dbVersion, dbCount] = await Promise.all([this.readLocalVersion(), this.db.items.count()]);

      if (manifest.version && manifest.version === dbVersion && dbCount > 0) {
        this.localCount.set(dbCount);
        this.localVersion.set(dbVersion);
        return { count: dbCount, version: manifest.version, alreadyUpToDate: true };
      }

      const payload = await this.downloadBundlePayload(manifest.url);
      await this.applyBundlePayload(payload);

      this.localCount.set(payload.count ?? payload.items.length);
      this.localVersion.set(payload.version);

      return { count: this.localCount(), version: payload.version, alreadyUpToDate: false };
    } finally {
      this.refreshing.set(false);
    }
  }

  private async executeSearchQuery(term: string, upper: string, lower: string, end: string, resultLimit: number): Promise<ItemProduct[]> {
    if (this.localCount() === 0) {
      return this.fetchSkusFromFirestore(term, upper, end, resultLimit);
    }

    // Indexed prefix query on the `product` key — fast, no table scan.
    const skuMatches = await this.db.items.where('product').between(upper, end, true, false).limit(resultLimit).toArray();
    if (skuMatches.length >= resultLimit) {
      return skuMatches;
    }

    // Only scan descriptions to fill the remaining slots. Skip rows already found above
    const seen = new Set(skuMatches.map((item) => item.product));
    const descMatches = await this.db.items
      .filter((item) => !seen.has(item.product) && (item.description?.toLowerCase().includes(lower) ?? false))
      .limit(resultLimit - skuMatches.length)
      .toArray();

    return [...skuMatches, ...descMatches];
  }

  private async fetchSkusFromFirestore(term: string, upper: string, end: string, resultLimit: number): Promise<ItemProduct[]> {
    const descEnd = buildPrefixEnd(term);
    const productQuery = query(
      collection(this.firestore, WH_PRODUCTS_COLLECTION),
      where('product', '>=', upper),
      where('product', '<', end),
      limit(resultLimit),
    );
    const descriptionQuery = query(
      collection(this.firestore, WH_PRODUCTS_COLLECTION),
      where('description', '>=', term),
      where('description', '<', descEnd),
      limit(resultLimit),
    );

    const [productSnap, descriptionSnap] = await Promise.all([getDocs(productQuery), getDocs(descriptionQuery)]);

    const merged = new Map<string, ItemProduct>();
    for (const doc of [...productSnap.docs, ...descriptionSnap.docs]) {
      const item = doc.data() as ItemProduct;
      merged.set(item.product, item);
    }

    return Array.from(merged.values()).slice(0, resultLimit);
  }

  private fetchBundleManifest(): Promise<BundleManifest> {
    return HttpUtils.get<BundleManifest>(this.environment.appConfig.fns, BUNDLE_ENDPOINT);
  }

  private downloadBundlePayload(signedUrl: string): Promise<BundlePayload> {
    return getRaw<BundlePayload>(signedUrl, 'json');
  }

  private async applyBundlePayload(payload: BundlePayload): Promise<void> {
    try {
      await withConnectionRetry('items.applyBundlePayload', () =>
        this.db.transaction('rw', this.db.items, this.db.meta, async () => {
          await this.db.items.clear();
          for (let offset = 0; offset < payload.items.length; offset += DEXIE_WRITE_CHUNK_SIZE) {
            const chunk = payload.items.slice(offset, offset + DEXIE_WRITE_CHUNK_SIZE);
            await this.db.items.bulkPut(chunk);
          }
          await this.db.meta.put({ key: META_VERSION_KEY, value: payload.version });
        }),
      );
    } catch (e) {
      const attempts = isConnectionError(e) ? 2 : 1;
      notifyPersistenceFailure(this.db.persistenceFailureHandler, { storeName: 'items_meta', operation: 'applyBundlePayload', error: e, attempts, happenedAt: new Date().toISOString() });
      throw e;
    }
  }

  private async readLocalVersion(): Promise<string | null> {
    const entry = await this.db.meta.get(META_VERSION_KEY);
    return entry?.value ?? null;
  }
}
